import React, { forwardRef, MouseEventHandler, Ref, useEffect, useState } from 'react';
import { ApiSanctionAlert } from '../api';
import { Avatar, Button } from '@salvapp/ui-components';
import { Dropdown } from 'react-bootstrap';
import '../alerts/AlertRowAssignment.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getUserId } from '../auth/authReducer';
import { AppState } from '../store/store';
import { assignScreeningAlert } from './screeningActions';
import Has from '../auth/Has';
import { CAN_MODIFY_ALERT_THROUGH_FILTER, CAN_MODIFY_SANCTION_ALERT } from '../auth/permissions';
import { DropdownWrapper } from '../common/DropdownWrapper';
import { InternalApiUserBasicDetails } from '../api-internal';
import { useAssignableUsers } from '../users/useAssignableUsers';

interface AlertRowAssignmentProps {
  alert: ApiSanctionAlert;
  defaultSize?: number;
  onChange: (user: InternalApiUserBasicDetails) => void;
  sizeClass?: string;
}

interface ToggleProps {
  onClick: MouseEventHandler;
}

export const ScreeningAlertRowAssignment = ({
  alert,
  defaultSize = 40,
  onChange,
  sizeClass = '',
}: AlertRowAssignmentProps) => {
  const dispatch = useDispatch();
  const { assignableUsers } = useAssignableUsers();
  const userId = useSelector((state: AppState) => getUserId(state));
  const user = assignableUsers?.find((u) => u.id === userId);
  const [displayedAssignments, setDisplayedAssignments] = useState(assignableUsers?.slice(0, defaultSize));

  useEffect(() => {
    setDisplayedAssignments(assignableUsers?.slice(0, defaultSize));
  }, [assignableUsers, defaultSize]);

  const Toggle = forwardRef(({ onClick }: ToggleProps, ref: Ref<HTMLButtonElement>) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      onClick(e);
    };

    return (
      <Has anyPermissions={[CAN_MODIFY_SANCTION_ALERT, CAN_MODIFY_ALERT_THROUGH_FILTER]}>
        {(can: boolean) =>
          can ? (
            (alert.assignee && (
              <Button
                variant='white'
                className={`d-flex flex-row align-items-center dropdown-toggle ${sizeClass}`}
                ref={ref}
                onClick={handleClick}
              >
                <Avatar email={alert.assignee.email} />
                <span className='ms-2'>{alert.assignee.name}</span>
              </Button>
            )) || (
              <Button
                variant='white'
                ref={ref}
                onClick={handleClick}
                className={`d-flex align-items-center dropdown dropdown-toggle ${sizeClass}`}
              >
                Assign
              </Button>
            )
          ) : (
            <div>
              {alert.assignee && (
                <div>
                  <Avatar email={alert.assignee.email} />
                  <span className='ms-2'>{alert.assignee.name}</span>
                </div>
              )}
            </div>
          )
        }
      </Has>
    );
  });

  Toggle.displayName = 'Toggle';

  const handleClick = (user: InternalApiUserBasicDetails) => {
    if (user.id) {
      dispatch(assignScreeningAlert(alert.id, user.id));
      onChange(user);
    }
  };

  const handleAssignToMe = async () => {
    if (user) handleClick(user);
  };

  const searchUsers = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value.toLowerCase();
    setDisplayedAssignments(
      assignableUsers?.filter((user) => {
        return (
          user.email && (user.name.toLocaleLowerCase().includes(query) || user.email.toLowerCase().includes(query))
        );
      })
    );
  };

  return (
    <DropdownWrapper className='assignee'>
      <Dropdown.Toggle as={Toggle} id='chooseAssignee' />
      <Dropdown.Menu align={{ sm: 'start' }} popperConfig={{ strategy: 'fixed' }}>
        <div className='row px-4'>
          <div className='col dropdown-search-assignee'>
            <input
              type='text'
              className='form-control form-control-sm'
              placeholder='Search ....'
              onChange={(e) => searchUsers(e)}
              autoFocus
            />
          </div>
          {user && (
            <Dropdown.Item className='col assign-self-button'>
              <Button size='medium' variant='outline-primary' onClick={handleAssignToMe}>
                Assign to me
              </Button>
            </Dropdown.Item>
          )}
        </div>
        <Dropdown.Divider />
        <div className='items'>
          {displayedAssignments?.map((user) => (
            <Dropdown.Item
              key={user.id}
              as='button'
              className='d-flex flex-row align-items-center dropdown-item-regular'
              onClick={() => handleClick(user)}
            >
              <Avatar email={user.email} />
              <div className='d-flex flex-column'>
                <div className='ms-2'>{user.name}</div>
                <div className='text-muted ms-2 small'>{user.email}</div>
              </div>
            </Dropdown.Item>
          ))}
        </div>
      </Dropdown.Menu>
    </DropdownWrapper>
  );
};

export default ScreeningAlertRowAssignment;
